import posthog from "posthog-js";

import { analyticsContext, isAnalyticsEnabled } from "./posthog";
import {
    captureWalletEvent,
    eventError,
    eventId,
    type EventOwner,
    type EventSurface,
    ownerForAddress,
    type WalletEvent,
    type WalletEventProperties,
    type WalletSource
} from "./walletEvents";

interface Attempt {
    properties: WalletEventProperties;
    started: number;
    settled: boolean;
    failed: boolean;
}

/** One owner for the connector lifecycle. UI visibility never cancels an attempt. */
export class WalletJourney {
    private flow = "";
    private entry_point: WalletSource = "unknown";
    private view: { id: string; surface: EventSurface; started: number; state: string } | null = null;
    private attempt: Attempt | null = null;
    private attemptNumber = 0;
    private wasConnecting = false;
    private address: string | null = null;
    private session: EventOwner = { properties: {} };
    private ready = false;
    private connectedAt = 0;
    private accessDecision: string | null = null;
    private disconnectPending = false;
    private disconnectOperation: WalletEventProperties | null = null;

    constructor(private readonly emit: typeof captureWalletEvent = captureWalletEvent) {}

    owner(): EventOwner {
        return { ...this.session, properties: { ...this.session.properties } };
    }
    context(): WalletEventProperties {
        return {
            entry_point: this.entry_point,
            connection_flow_id: this.flow || undefined,
            ...this.attempt?.properties,
            ...this.session.properties
        };
    }
    open(entry_point: WalletSource, surface: EventSurface, clicked: boolean): void {
        if (this.view) return;
        this.flow = eventId();
        this.entry_point = entry_point;
        this.attemptNumber = 0;
        this.view = { id: eventId(), surface, started: performance.now(), state: "selector" };
        if (clicked) this.emit("wallet_connect:requested", { entry_point, connection_flow_id: this.flow });
        // Actual opening and initial state are recorded by the visible screen effect.
    }
    viewState(state: string, properties: WalletEventProperties = {}): void {
        if (!this.view) return;
        const key = JSON.stringify([state, properties.available_providers, properties.installed_providers]);
        if (this.lastView === key) return;
        const context = {
            ...(this.attempt?.properties.connection_flow_id === this.flow ? this.attempt.properties : {}),
            entry_point: this.entry_point,
            connection_flow_id: this.flow,
            view_id: this.view.id,
            surface: this.view.surface
        };
        if (this.lastView === null) this.emit("wallet_connect:modal_opened", { ...context, initial_state: state });
        this.lastView = key;
        this.view.state = state;
        this.emit("wallet_connect:step_viewed", { ...context, ...properties, state });
    }
    private lastView: string | null = null;
    close(reason = "unknown"): void {
        if (!this.view) return;
        this.emit("wallet_connect:modal_closed", {
            entry_point: this.entry_point,
            connection_flow_id: this.flow,
            view_id: this.view.id,
            surface: this.view.surface,
            last_state: this.view.state,
            close_method: reason === "escape" ? "escape_key" : reason,
            attempt_pending: !!this.attempt && !this.attempt.settled,
            duration_ms: performance.now() - this.view.started
        });
        this.view = null;
        this.lastView = null;
    }
    start(
        provider: string,
        method: WalletEventProperties["connection_method"] = "unknown",
        mode: "manual" | "restore" = "manual"
    ): string {
        if (mode === "restore") {
            this.flow = eventId();
            this.entry_point = "auto_restore";
            this.attemptNumber = 0;
        }
        const properties: WalletEventProperties = {
            entry_point: this.entry_point,
            connection_flow_id: this.flow,
            connection_attempt_id: eventId(),
            wallet_provider: provider,
            connection_method: method,
            connection_mode: mode,
            attempt_number: ++this.attemptNumber
        };
        this.attempt = { properties, started: performance.now(), settled: false, failed: false };
        if (mode === "manual") this.emit("wallet_connect:provider_selected", properties);
        this.emit("wallet_connect:started", properties);
        return properties.connection_attempt_id!;
    }
    failed(id: string, error: unknown): void {
        if (this.attempt?.properties.connection_attempt_id !== id || this.attempt.settled) return;
        this.attempt.settled = true;
        this.attempt.failed = true;
        this.emit("wallet_connect:failed", {
            ...this.attempt.properties,
            ...eventError(error),
            failure_stage: "provider_connect",
            modal_open: !!this.view,
            duration_ms: performance.now() - this.attempt.started
        });
    }
    restoreTimeout(): void {
        if (this.attempt?.properties.connection_mode === "restore")
            this.failed(this.attempt.properties.connection_attempt_id!, new Error("timeout"));
    }
    observe(address: string | null, connecting: boolean, provider: string): void {
        if (!address && connecting && !this.attempt && !this.view) this.start(provider, "unknown", "restore");
        if (
            !address &&
            this.wasConnecting &&
            !connecting &&
            this.attempt?.properties.connection_mode === "restore" &&
            !this.attempt.settled
        ) {
            this.failed(this.attempt.properties.connection_attempt_id!, new Error("Restore failed"));
        }
        this.wasConnecting = connecting;
        if (address === this.address) return;
        if (!address) {
            this.emit(
                "wallet_disconnect:completed",
                { ...this.disconnectOperation, trigger: this.disconnectOperation?.trigger ?? "unknown" },
                this.owner()
            );
            this.address = null;
            this.attempt = null;
            this.disconnectOperation = null;
            this.session = { properties: {} };
            this.ready = false;
            this.accessDecision = null;
            if (isAnalyticsEnabled()) {
                posthog.reset();
                posthog.register(analyticsContext());
            }
            return;
        }
        const changed = this.address !== null;
        if (changed && isAnalyticsEnabled()) {
            posthog.reset();
            posthog.register(analyticsContext());
        }
        this.address = address;
        this.ready = false;
        this.accessDecision = null;
        this.connectedAt = performance.now();
        this.session = ownerForAddress(address, {
            wallet_session_id: eventId(),
            wallet_provider: provider,
            entry_point: changed ? "provider" : (this.attempt?.properties.entry_point ?? "auto_restore")
        });
        const owner = this.owner();
        void owner.identity
            ?.then((identity) => {
                if (
                    identity &&
                    this.session.properties.wallet_session_id === owner.properties.wallet_session_id &&
                    isAnalyticsEnabled()
                ) {
                    posthog.identify(identity, { wallet_provider: provider });
                }
            })
            .catch(() => undefined);
        if (changed) {
            this.attempt = null;
            this.emit("wallet:account_changed", { change_source: "provider" }, owner);
        } else {
            const attempt = this.attempt;
            const missingStart = !attempt ? { connection_flow_id: eventId(), connection_attempt_id: eventId() } : {};
            this.emit(
                "wallet_connect:completed",
                {
                    ...missingStart,
                    ...attempt?.properties,
                    connection_mode: attempt?.properties.connection_mode ?? "restore",
                    entry_point: attempt?.properties.entry_point ?? "auto_restore",
                    start_observed: !!attempt,
                    late_result: attempt?.failed ?? false,
                    modal_open: !!this.view,
                    duration_ms: attempt ? performance.now() - attempt.started : undefined
                },
                owner
            );
            if (attempt) attempt.settled = true;
        }
    }
    access(result: "granted_public" | "denied" | "error", ready: boolean): void {
        if (!this.address) return;
        if (this.accessDecision !== result) {
            this.accessDecision = result;
            const properties = { ...this.context(), duration_ms: performance.now() - this.connectedAt };
            if (result === "error")
                this.emit(
                    "wallet:access_check_failed",
                    { ...properties, error_code: "access_check_failed", error_category: "network" },
                    this.owner()
                );
            else this.emit("wallet:access_check_completed", { ...properties, decision: result }, this.owner());
        }
        if (ready && result === "granted_public" && !this.ready) {
            this.ready = true;
            this.emit(
                "wallet:ready",
                { ...this.context(), ready_duration_ms: performance.now() - this.connectedAt },
                this.owner()
            );
        }
    }
    async disconnect(
        disconnect: () => Promise<void>,
        trigger: "user_action" | "terms_rejected",
        guard?: () => boolean | Promise<boolean>,
        properties: WalletEventProperties = {},
        dirty = false
    ): Promise<boolean> {
        if (this.disconnectPending) return false;
        this.disconnectPending = true;
        const owner = this.owner();
        const operation = { ...properties, operation_id: eventId(), trigger };
        this.disconnectOperation = operation;
        const emit = (event: WalletEvent, extra: WalletEventProperties = {}): void =>
            this.emit(event, { ...operation, ...extra }, owner);
        emit("wallet_disconnect:requested");
        try {
            if (guard) {
                if (dirty) emit("wallet_disconnect:confirmation_viewed");
                const allowed = await guard();
                if (!allowed) {
                    emit("wallet_disconnect:canceled", { reason: "confirmation_dismissed" });
                    this.disconnectOperation = null;
                    return false;
                }
                if (dirty) emit("wallet_disconnect:confirmed");
            }
            emit("wallet_disconnect:started");
            await disconnect();
            return true;
        } catch (error) {
            if (this.address) emit("wallet_disconnect:failed", eventError(error));
            this.disconnectOperation = null;
            return false;
        } finally {
            this.disconnectPending = false;
        }
    }
}

export const walletJourney = new WalletJourney();
